import { getLogger } from '../../services/logging/logger';

/**
 * Severity tones accepted by the Spark inline banner. Values match the strings
 * returned by GET /sparkBannerMessaging, so the enum doubles as a wire-format
 * contract: changing a member value is a breaking API change.
 */
export enum InlineMessageSeverity {
  INFORMATION = 'Information',
  WARNING = 'Warning',
  ALERT = 'Alert',
}

const VALID_SEVERITIES: ReadonlySet<string> = new Set(Object.values(InlineMessageSeverity));

/**
 * Narrows the raw `severity` string returned by the API to `InlineMessageSeverity`.
 *
 * Unknown values are coerced to `INFORMATION` so the banner stays renderable when
 * the backend ships a new severity before the frontend knows about it. We log a
 * warning so schema drift is visible in Datadog rather than failing silently.
 */
export const normalizeSparkBannerSeverity = (
  raw: string | null | undefined,
): InlineMessageSeverity => {
  if (raw != null && VALID_SEVERITIES.has(raw)) {
    return raw as InlineMessageSeverity;
  }
  getLogger().warn(
    `Unknown spark banner severity received from API: ${JSON.stringify(
      raw,
    )}. Falling back to 'Information'.`,
  );
  return InlineMessageSeverity.INFORMATION;
};

/**
 * Action performed by the banner CTA button. Values match the strings returned
 * by GET /sparkBannerMessaging, so the enum doubles as a wire-format contract:
 * changing a member value is a breaking API change.
 */
export enum SparkBannerCallToAction {
  /** Opens the Spark chat widget so the user can analyze the sessions. */
  SPARK_CHAT = 'spark_chat',
  /** Switches the Charger History panel to the Charger History tab. */
  CHARGER_HISTORY = 'charger_history',
}

const VALID_CALL_TO_ACTIONS: ReadonlySet<string> = new Set(Object.values(SparkBannerCallToAction));

/**
 * Narrows the raw `callToActionType` string returned by the API to
 * `SparkBannerCallToAction`.
 *
 * An absent/null value means "no CTA". Unknown non-null values are also coerced
 * to `null` (hiding the button) so the banner stays renderable when the backend
 * ships a new CTA before the frontend knows about it; we log a warning so schema
 * drift is visible in Datadog rather than failing silently.
 */
export const normalizeSparkBannerCallToAction = (
  raw: string | null | undefined,
): SparkBannerCallToAction | null => {
  if (raw == null) {
    return null;
  }
  if (VALID_CALL_TO_ACTIONS.has(raw)) {
    return raw as SparkBannerCallToAction;
  }
  getLogger().warn(
    `Unknown spark banner callToActionType received from API: ${JSON.stringify(
      raw,
    )}. Hiding the CTA button.`,
  );
  return null;
};

/** Response from GET /internal/core/v2/chargers/:chargerId/sparkBannerMessaging (Spark tab inline banner). */
export interface SparkBannerMessagingResponse {
  severity: InlineMessageSeverity;
  leadMessage: string;
  supportingMessage: string;
  /** Lookback window in days for Spark when the user uses the banner CTA. */
  lookbackDays: number;
  /** Which action the CTA button performs, or `null` when no CTA should be shown. */
  callToActionType: SparkBannerCallToAction | null;
}
